import React from 'react';
import { AutoSizer, Table, Column } from 'react-virtualized';
import { useTranslation } from 'react-i18next';
import { EllipsisOutlined, LockOutlined } from '@ant-design/icons';
import { Dropdown, Spin, Tooltip } from 'antd';

const ROW_HEIGHT = 48;
const COL_WIDTH = 200;

type PreviewTableProps = {
  setDrawerVisible: (bool: boolean, colId: number | null, newCol?: boolean) => void;
  templateColumns: {
    id: number;
    label: string;
    number: number;
    dataKey: string;
    locked: number;
  }[];
  fetching: boolean;
  fetchingPreview: boolean;
  preview: any[];
};

const PreviewTable: React.FC<PreviewTableProps> = ({
  setDrawerVisible,
  templateColumns,
  fetching,
  fetchingPreview,
  preview,
}) => {
  const { t } = useTranslation();

  const [rows, setRows] = React.useState<any>([]);

  React.useEffect(() => {
    setRows(preview);
  }, [preview]);

  const getRow = ({ index }: any) => {
    return rows[index];
  };

  const columnItems = (colId: number) => [
    {
      label: t('exportBuilderAdvanced:editColumn'),
      key: 'ebp_edit-col',
      onClick: () => setDrawerVisible(true, colId),
    },
  ];

  const headerRenderer = ({ label, columnData }: any) => {
    return (
      <div className="flex items-center">
        <div className="flex items-center mr-2 w-full overflow-hidden">
          <Tooltip title={label}>
            <span
              data-testid="preview-table-header-label"
              className="preview-table__header-title__truncate overflow-hidden overflow-ellipsis"
            >
              {label}
            </span>
          </Tooltip>
        </div>
        {columnData.locked ? (
          <LockOutlined />
        ) : (
          <Dropdown menu={{ items: columnItems(columnData.id) }} trigger={['click']}>
            <EllipsisOutlined />
          </Dropdown>
        )}
      </div>
    );
  };

  if (!templateColumns) {
    return null;
  }

  if (fetching)
    return (
      <div className="h-full flex justify-center pt-8">
        <Spin />
      </div>
    );

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 border border-solid border-gray-400 rounded overflow-x-auto">
        {fetchingPreview ? (
          <div className="h-full flex justify-center pt-8">
            <Spin />
          </div>
        ) : (
          <AutoSizer>
            {({ width, height }) => {
              const allColWidth = templateColumns.length * COL_WIDTH;
              return (
                <Table
                  width={allColWidth < width ? width - 2 : allColWidth}
                  height={height - 20}
                  headerHeight={ROW_HEIGHT}
                  rowHeight={ROW_HEIGHT}
                  rowCount={rows.length}
                  rowGetter={getRow}
                >
                  {templateColumns.map(col => (
                    <Column
                      key={col.id}
                      label={col.label}
                      dataKey={col.dataKey}
                      columnData={{ id: col.id, locked: col.locked }}
                      width={COL_WIDTH}
                      headerRenderer={headerRenderer}
                    />
                  ))}
                </Table>
              );
            }}
          </AutoSizer>
        )}
      </div>
    </div>
  );
};

export default PreviewTable;
